import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  MousePointerClick, 
  TrendingUp, 
  Calendar,
  Package,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAffiliateStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/affiliate-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="affiliate-stats-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-sky-400" />
            Statistici Click-uri Afiliate
          </h1>
          <p className="text-slate-400 mt-1">
            Monitorizează click-urile pe butoanele "Cumpără"
          </p>
        </div>
        <Button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizează
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-sky-500/20 rounded-xl flex items-center justify-center">
              <MousePointerClick className="w-6 h-6 text-sky-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Total Click-uri</p>
              <p className="text-3xl font-bold text-white">{stats?.total_clicks || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Click-uri Azi</p>
              <p className="text-3xl font-bold text-white">{stats?.today_clicks || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Produse cu Click-uri</p>
              <p className="text-3xl font-bold text-white">{stats?.product_stats?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Chart */}
      {stats?.daily_clicks && stats.daily_clicks.length > 0 && (
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-sky-400" />
            Ultimele 7 Zile
          </h2>
          <div className="flex items-end gap-2 h-32">
            {stats.daily_clicks.map((day, idx) => {
              const maxClicks = Math.max(...stats.daily_clicks.map(d => d.clicks), 1);
              const height = (day.clicks / maxClicks) * 100;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className="w-full bg-sky-500 rounded-t-lg transition-all hover:bg-sky-400"
                    style={{ height: `${Math.max(height, 4)}%` }}
                    title={`${day.clicks} click-uri`}
                  ></div>
                  <span className="text-xs text-slate-500">
                    {new Date(day.date).toLocaleDateString('ro-RO', { weekday: 'short' })}
                  </span>
                  <span className="text-xs text-slate-400 font-medium">{day.clicks}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Products Table */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-sky-400" />
            Top Produse după Click-uri
          </h2>
        </div>
        
        {stats?.product_stats && stats.product_stats.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left px-6 py-3 text-slate-400 text-sm font-medium">#</th>
                  <th className="text-left px-6 py-3 text-slate-400 text-sm font-medium">Produs</th>
                  <th className="text-center px-6 py-3 text-slate-400 text-sm font-medium">Click-uri</th>
                  <th className="text-right px-6 py-3 text-slate-400 text-sm font-medium">Ultimul Click</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {stats.product_stats.map((product, idx) => (
                  <tr key={product.product_id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4 text-slate-500 font-medium">{idx + 1}</td>
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">{product.product_name}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 bg-sky-500/20 text-sky-400 px-3 py-1 rounded-full text-sm font-medium">
                        <MousePointerClick className="w-3 h-3" />
                        {product.total_clicks}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-slate-400 text-sm">
                      {new Date(product.last_click).toLocaleDateString('ro-RO', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <MousePointerClick className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Încă nu există click-uri înregistrate</p>
            <p className="text-slate-500 text-sm mt-1">
              Click-urile pe butoanele "Cumpără" vor apărea aici
            </p>
          </div>
        )}
      </div>

      {/* Recent Clicks */}
      {stats?.recent_clicks && stats.recent_clicks.length > 0 && (
        <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-sky-400" />
              Ultimele Click-uri
            </h2>
          </div>
          
          <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
            {stats.recent_clicks.slice(0, 20).map((click, idx) => (
              <div key={idx} className="px-6 py-3 flex items-center justify-between hover:bg-slate-700/30">
                <div>
                  <span className="text-white font-medium">{click.product_name}</span>
                </div>
                <span className="text-slate-400 text-sm">
                  {new Date(click.clicked_at).toLocaleDateString('ro-RO', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliateStats;
